import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner } from "react-bootstrap";
import { siteLogo as logo } from "../../const";
import moment from "moment";
import SideMenu from "../../Components/SideMenu";
import { postApi } from "../../helpers/apiStructure";
import LinkifiedText from "../../Components/LinkifiedText";

const getMessageTypePreview = (messageType) => {
  switch (messageType) {
    case "image": return "Photo";
    case "sticker": return "Sticker";
    case "video": return "Video";
    case "audio": return "Audio";
    case "file": return "Attachment";
    default: return "";
  }
};

const getChatPreviewText = (message) => {
  const content = String(message?.content || "").replace(/\s+/g, " ").trim();
  return content || getMessageTypePreview(message?.messageType);
};

const ADMIN_CHAT_NAV_KEY = "adminChatUsersPaginationState";
let adminPendingNavRestore = false;

const AdminChatUsers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  const queryParameters = new URLSearchParams(location.search);
  const agentList = queryParameters.get("agent");
  const specificUserId = queryParameters.get("specificUserId");

  const scrollRestoredRef = useRef(false);
  const skipNextResetRef = useRef(false);

  const getInitialPage = () => {
    if (!adminPendingNavRestore) return 1;
    try {
      const saved = JSON.parse(sessionStorage.getItem(ADMIN_CHAT_NAV_KEY));
      if (saved?.page && saved?.userId === specificUserId) {
        skipNextResetRef.current = true;
        return saved.page;
      }
    } catch {}
    return 1;
  };

  const getInitialTab = () => {
    if (!adminPendingNavRestore) return "all";
    try {
      const saved = JSON.parse(sessionStorage.getItem(ADMIN_CHAT_NAV_KEY));
      if (saved?.tab && saved?.userId === specificUserId) return saved.tab;
    } catch {}
    return "all";
  };

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(getInitialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminPendingNavRestore = false;
  }, []);

  useEffect(() => {
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }
    sessionStorage.removeItem(ADMIN_CHAT_NAV_KEY);
    scrollRestoredRef.current = false;
    setUsers([]);
    setPage(1);
    setTotalPages(0);
    setActiveTab("all");
  }, [location]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await postApi(
          `${baseUrl}/api/messages/specific-user-chat`,
          "post",
          { specificUserId, page, filter: activeTab }
        );
        setUsers(response?.data || []);
        if (response?.total != null && response?.pageSize) {
          setTotalPages(Math.ceil(response.total / response.pageSize));
        }
      } catch (err) {
        console.error("AdminChatUsers fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (specificUserId) fetchUsers();
  }, [specificUserId, page, activeTab, baseUrl]);

  useEffect(() => {
    if (scrollRestoredRef.current || !users.length) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(ADMIN_CHAT_NAV_KEY));
      if (saved?.scrollY && saved?.userId === specificUserId) {
        setTimeout(() => window.scrollTo({ top: saved.scrollY, behavior: "instant" }), 60);
        scrollRestoredRef.current = true;
      }
    } catch {}
  }, [users, specificUserId]);

  const saveNavState = () => {
    try {
      adminPendingNavRestore = true;
      sessionStorage.setItem(
        ADMIN_CHAT_NAV_KEY,
        JSON.stringify({ page, scrollY: window.scrollY, tab: activeTab, userId: specificUserId })
      );
    } catch {}
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const delta = 2;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        pages.push(i);
      }
    }
    const buttons = [];
    let prev = null;
    for (const p of pages) {
      if (prev !== null && p - prev > 1) {
        buttons.push(<span key={`ellipsis-${p}`} className="chat-page-ellipsis">…</span>);
      }
      buttons.push(
        <button
          key={p}
          className={`chat-page-btn${page === p ? " active" : ""}`}
          onClick={() => setPage(p)}
          disabled={loading}
        >
          {p}
        </button>
      );
      prev = p;
    }
    return (
      <Row className="mt-3 mb-2">
        <Col className="d-flex justify-content-center">
          <div className="chat-pagination">
            <button className="chat-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>
              <i className="bi bi-chevron-left"></i>
            </button>
            {buttons}
            <button className="chat-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <SideMenu />
      <div className="mt-extra content container-fluid">
        <div className="p-2 mbl-pedding-10 min-vh-100">
          <div className="chat-tabs-wrapper">
            <div className="chat-tabs-bar">
              {["all", "unread", "read"].map((tab) => (
                <button
                  key={tab}
                  className={`chat-tab-btn${activeTab === tab ? " active" : ""}`}
                  onClick={() => { setActiveTab(tab); setPage(1); }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : users.length > 0 ? (
            <>
              {users.map((item, index) => (
                <Row
                  key={item?.userId || index}
                  onClick={() => {
                    saveNavState();
                    navigate(
                      `/admin-chat?id=${item?.userId}&firstUserid=${specificUserId}&name=${encodeURIComponent(item?.username || `${item?.firstName} ${item?.lastName}`)}&img=${item?.profile || ""}&time=${item?.lastSeen || ""}&agent=${agentList || ""}`
                    );
                  }}
                  className={`mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer ${
                    item?.lastMessage && !item.lastMessage.isRead && item?.lastMessage?.sender?.toString() !== specificUserId
                      ? "unread-message-borader" : ""
                  }`}
                >
                  <Col md={2} sm={2} xs={2}>
                    <div className="profile-img-wrapper">
                      <img
                        className="profile-img"
                        src={item?.profile || logo}
                        alt={item?.firstName || "user"}
                      />
                      {(item?.roleSlug === "team_leader" || item?.roleSlug === "branch_leader") && (
                        <span className="verified-badge">
                          <i className="bi bi-patch-check-fill"></i>
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">
                      {item?.username || `${item?.firstName} ${item?.lastName}`}
                    </p>
                    {getChatPreviewText(item?.lastMessage) && (
                      <p className="mb-0">
                        <LinkifiedText text={getChatPreviewText(item?.lastMessage)} />
                      </p>
                    )}
                  </Col>
                  {item?.userId !== localStorage.getItem("userid") && (
                    <Col className="px-0" md={2} sm={2} xs={2}>
                      <p className={`mb-0 ${!item?.lastMessage?.isRead && item?.lastMessage?.sender?.toString() !== specificUserId ? "text-success" : ""}`}>
                        <b>
                          {item?.lastMessage?.createdAt &&
                            moment(item?.lastMessage?.createdAt).format("hh:mm A")}
                        </b>
                      </p>
                    </Col>
                  )}
                </Row>
              ))}
              {renderPagination()}
            </>
          ) : (
            <div className="text-center mt-5 d-flex justify-content-center align-items-center">
              {activeTab !== "all" ? (
                <h4 className="text-muted">No {activeTab} chats.</h4>
              ) : (
                <h4 className="text-danger">No users found to chat with.</h4>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminChatUsers;
